import CustomerApiService from "../../../CustomerApiService";
import { sortByKey } from "../../shared/sortByKey";

/**
 * Candidate Assignments service — ported almost verbatim from
 * apps/vault-frontend/src/features/candidate-assignments/service.ts (spec §6.2/§6.3). The only
 * changes: `ApiService` -> `CustomerApiService` (cookie-session client, spec §2.1), and every
 * path gets the `/ab-vault-candidate-assignments` interface-namespace prefix (spec §5.2's API
 * namespace convention — vault-api mounted these routes unprefixed at its API root; customer-api
 * mounts them under `/api/ab-vault-candidate-assignments/*`).
 *
 * Auth rides along automatically via the `customer_session` cookie (CustomerApiService sets
 * `credentials: "include"` on every request); a 401 dispatches `SESSION_EXPIRED_EVENT` (picked up
 * by every mounted `useInterfaceAccess`, see its own doc comment) instead of vault's hard
 * `/ui/401` redirect. Bodies are BARE payloads (no envelope).
 */

const PREFIX = "/ab-vault-candidate-assignments";

/** A user/group/candidate row: `{ id, name }`. */
export interface EntityItem {
  id: string;
  name: string;
}

/** Permission tier from `GET /api/ab-vault-candidate-assignments/user/info`. */
export type VaultRights = "admin" | "user" | "partner";

/** Logged-in user info (`GET /user/info`). */
export interface UserInfo {
  id: string;
  name: string;
  rights: VaultRights;
  groups?: string[];
}

/**
 * The selected entity whose assignments are being edited.
 *
 * `type` is `"users"` | `"groups"`. The `"groups"` discriminant is retained for backward
 * compatibility (it keys the `/assignments/groups/{id}` read and the stored `groups` attribute),
 * but it now carries a TEAM id — the picker lists teams (`getTeams`), not the deprecated iconik
 * groups.
 */
export interface SelectedEntity {
  value?: EntityItem;
  type: "users" | "groups";
}

/** The `parts` diff posted to `POST /acl`. */
export interface AssignmentParts {
  toAdd: EntityItem[];
  toRemove: EntityItem[];
}

/** A raw `/assignments/{type}/{id}` row (iconik assignment). */
interface AssignmentRow {
  id: string;
}

/** A raw iconik field option (`/assets/fields/Candidate` -> `.options`). */
interface FieldOption {
  label: string;
  value: string;
}

interface FieldDefinition {
  options?: FieldOption[];
}

/** GET `/user/info` — logged-in user incl. `rights` (passed through). */
export async function getUserInfo(): Promise<UserInfo> {
  return CustomerApiService.fetch<UserInfo>("GET", `${PREFIX}/user/info`);
}

/**
 * GET `/assignments/assignment` — the CALLER's own assignment(s).
 * Legacy `AbIconik.getAssignments` maps `t => { label: t.id, value: t.id }`;
 * the regular-user init then maps those into `{ id: value, name: value }`.
 */
export async function getOwnAssignments(): Promise<EntityItem[]> {
  const result = await CustomerApiService.fetch<AssignmentRow[]>(
    "GET",
    `${PREFIX}/assignments/assignment`
  );
  return Array.isArray(result)
    ? result.map((t) => ({ id: t.id, name: t.id }))
    : [];
}

/**
 * GET `/assignments/{type}/{id}` — assignments for a chosen entity.
 * `type` is `"users"` | `"groups"`. Maps `n => { id: n.id, name: n.id }`.
 */
export async function getAssignmentForEntity(
  type: "users" | "groups",
  id: string
): Promise<EntityItem[]> {
  const result = await CustomerApiService.fetch<AssignmentRow[]>(
    "GET",
    `${PREFIX}/assignments/${type}/${id}`
  );
  return Array.isArray(result)
    ? result.map((n) => ({ id: n.id, name: n.id }))
    : [];
}

/** GET `/users` — all iconik users; sorted by `name` (ci). */
export async function getUsers(): Promise<EntityItem[]> {
  const result = await CustomerApiService.fetch<EntityItem[]>(
    "GET",
    `${PREFIX}/users`
  );
  return Array.isArray(result)
    ? result.slice().sort(sortByKey<EntityItem>("name"))
    : [];
}

/**
 * GET `/teams` — all iconik teams; sorted by `name` (ci).
 *
 * MODERNIZATION: iconik's groups list API is deprecated, so the picker now lists TEAMS
 * (`GET /teams`) instead of the old `GET /groups`. The selected entity `type` and the stored
 * assignment attribute are still named `groups` for backward compatibility — they now carry team
 * ids.
 */
export async function getTeams(): Promise<EntityItem[]> {
  const result = await CustomerApiService.fetch<EntityItem[]>(
    "GET",
    `${PREFIX}/teams`
  );
  return Array.isArray(result)
    ? result.slice().sort(sortByKey<EntityItem>("name"))
    : [];
}

/**
 * GET `/assets/fields/Candidate` -> `.options`. Legacy `init()` keeps only options with a truthy
 * `value`, maps `{ id: value, name: label }`, and sorts by `name` (ci).
 */
export async function getCandidates(): Promise<EntityItem[]> {
  const def = await CustomerApiService.fetch<FieldDefinition>(
    "GET",
    `${PREFIX}/assets/fields/Candidate`
  );
  const options = def.options ?? [];
  return options
    .filter((o) => !!o.value)
    .map((o) => ({ id: o.value, name: o.label }))
    .sort(sortByKey<EntityItem>("name"));
}

/**
 * POST `/acl` — persist the assignment diff (legacy `service.save`).
 * Body `{ entity, parts }`. Returns whatever the API returns (ignored on the happy path; the
 * screen reloads the entity afterwards).
 */
export async function saveAssignment(
  entity: SelectedEntity,
  parts: AssignmentParts
): Promise<unknown> {
  return CustomerApiService.fetch("POST", `${PREFIX}/acl`, { entity, parts });
}

// ---------------------------------------------------------------------------
// Pure helpers (formerly `service.filter` / `service.splitParts`)
// ---------------------------------------------------------------------------

/**
 * `service.splitParts()` — diff `selected` against the originally-loaded `assigned` set.
 * Membership is compared by `id`, so a freshly-loaded object (different instance, same `id`)
 * diffs correctly.
 */
export function splitParts(
  selected: EntityItem[],
  assigned: EntityItem[] | undefined
): AssignmentParts {
  const assignedList = assigned ?? [];
  const hasId = (arr: EntityItem[], item: EntityItem): boolean =>
    arr.some((x) => x.id === item.id);
  const toAdd = selected.filter((s) => !hasId(assignedList, s));
  const toRemove = assigned ? assigned.filter((a) => !hasId(selected, a)) : [];
  return { toAdd, toRemove };
}

/**
 * `service.filter(criteria, listKey)` — case-insensitive substring filter by `name`, plus (for
 * candidates) the exclusion of already-selected ids and a `name`-ascending sort.
 */
export function filterEntities(
  list: EntityItem[],
  name: string,
  options?: { excludeSelected?: EntityItem[] }
): EntityItem[] {
  const lower = name.toLowerCase();
  let result = list.slice();
  if (name) {
    result = result.filter(
      (item) => item.name.toLowerCase().indexOf(lower) !== -1
    );
  }
  const exclude = options?.excludeSelected;
  if (exclude && exclude.length) {
    const ids = new Set(exclude.map((s) => s.id));
    result = result.filter((item) => !ids.has(item.id));
  }
  return result.sort(sortByKey<EntityItem>("name"));
}

/** Tier predicates. */
export const isNotUser = (rights: VaultRights | null): boolean =>
  rights !== "user";
export const isAdmin = (rights: VaultRights | null): boolean =>
  rights === "admin";
